/**
 * Modular figures: a body is a stack of layers (the bare body, then underwear, clothes, hair,
 * headwear, weapon…), and every layer is a set of pieces hung on the skeleton's joints. A piece
 * knows its depth, so the whole figure is drawn back to front by sorting pieces from all layers
 * together: a far sleeve stays behind the torso and a near sleeve in front of it whatever layer it
 * came from.
 *
 * Model 0 is the bare mannequin (no hair, plain underwear) that clothes are designed on.
 */
import { shade, skinShade, tones } from '../palette';
import { ART_CX, BODY_EXTRA, FRAMES, PAD_TOP, type Pose, type View, WALK_FRAMES } from '../sprites';
import { DEFAULT_HAIR, type Grid, HAIR_BY_ID, type HairAnchors, type HairStyle } from './hair';
import { HEADWEAR_BY_ID } from './headwear';
import { gridFor } from './leftArt';
import { type FaceLook, faceStamps, type Mood } from './face';
import { type FineSlot, fineFace, isFinePart } from './face2';
import {
  drawHand,
  drawHandArt,
  type HandShape,
  handShape,
  handSprite,
  type HandSprite,
  handTones,
} from './hands';
import { type FineSprite, REF_HEAD } from './head2';
import { gazeFace } from './gaze';
import { HEAD } from './heads';
import { composeReal } from './realism';
import { composeNative, hasNative, type NativePaint } from './outfit2x/native';
import { BONES, GROUND, type Joint, type JointName, type Side, type Skeleton, skeleton } from './skeleton';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

/** One drawable piece of a layer, placed by depth among all the figure's pieces. */
export interface Piece {
  z: number;
  draw: (g: G) => void;
  /**
   * Drawn at the figure's full resolution (`res` canvas pixels per art pixel) after it is
   * enlarged, finished as it is, over its `draw` (a hand, the face's fine features).
   */
  fine?: (g: G, res: number) => void;
  /** A hand closed round a held weapon: laid back over the weapon drawn at full resolution. */
  grip?: boolean;
  /**
   * A hand (hands.ts): its full-resolution drawing goes over whatever lies behind it and under
   * whatever lies in front, so its fingers and thumb may reach past its art-resolution pixels.
   */
  hand?: boolean;
  /**
   * Depth per pixel (in the piece's own drawing space, `res` pixels per art pixel), for a long
   * thing that runs toward or away from the camera — a blade, a scabbard — so its near end passes
   * in front of the body and its far end behind. Pieces drawn at full resolution (weapons) are laid
   * in pixel by pixel against the depth of the figure under them; left out, `z` is used throughout.
   */
  depth?: (x: number, y: number) => number;
  /**
   * Painted at full resolution by itself (lane C0-OUTFIT2X, outfit2x/native.ts): `draw` gives the shape in role
   * colours, the painter the pixels.
   */
  native?: NativePaint;
}

/**
 * What the layers of one figure tell each other while it is composed: where headwear attaches
 * (set by the hairstyle) and which hair parts are hidden (by a hat, say).
 */
export interface Fit {
  anchors: HairAnchors;
  hidden: ReadonlySet<'bun' | 'tail'>;
  /** Drawing at this many pixels per design pixel (weapons only; the rest is drawn at 1 and scaled). */
  res?: number;
}

/** A layer turns a skeleton into pieces (a garment returns its torso, sleeves and legs). */
export interface Layer {
  id: string;
  label: string;
  pieces: (sk: Skeleton, fit: Fit) => Piece[];
  /** Anchor points this layer provides for the view (hairstyles provide the crown and buns). */
  anchors?: (view: View) => HairAnchors;
  /** Hair parts this layer hides. */
  hides?: readonly ('bun' | 'tail')[];
  /** The hairstyle's id and colour (on the hair layer). */
  hair?: { style: string; colour: string };
  /** Some of its pieces are painted at full resolution (`Piece.native`, lane C0-OUTFIT2X). */
  native?: boolean;
}

// ── drawing helpers ──

export function px(g: G, x: number, y: number, w: number, h: number, c: string): void {
  g.fillStyle = c;
  g.fillRect(Math.round(x), Math.round(y), w, h);
}

/**
 * A tapering limb segment from `a` to `b` as a chain of square dabs `w0`→`w1` wide, with a shade
 * edge on its lower side so it reads as round.
 */
export function segment(g: G, a: Pt, b: Pt, w0: number, w1: number, fill: string, shade: string): void {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
  for (const pass of [0, 1])
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const w = Math.max(1, Math.round(w0 + (w1 - w0) * t));
      const h = Math.floor(w / 2);
      const x = Math.round(a.x + (b.x - a.x) * t) - h;
      const y = Math.round(a.y + (b.y - a.y) * t) - h;
      px(g, x, y + (pass ? 0 : 1), w, w, pass ? fill : shade);
    }
}

/**
 * A limb of tapering segments (`[from, to, w0, w1]`, as `segment` takes them) drawn as one shape,
 * shaded along its whole edge away from the light (left and underneath). `segment`'s offset shade
 * leaves a lone pixel at each step of a slanting edge, which reads as a blemish where a limb lies
 * over the body; one unbroken line keeps the limb apart from the skin behind it.
 */
export function limb(
  g: G,
  parts: readonly (readonly [Pt, Pt, number, number])[],
  fill: string,
  shade: string,
  /**
   * The screen side the limb's outer edge is on (−1 left, 1 right; 0 neither): where it narrows
   * (at the elbow, down the shin) it loses its pixel on the inner side, so the outline away from
   * the body runs smooth instead of stepping in at every joint on one side of the figure only.
   */
  outer = 0,
  /**
   * An arm lying against the body: its upper arm's edge toward the body is shaded too, so it parts
   * from the torso in one line from the shoulder down into the armpit, on either side of the figure
   * (the edge away from the light is shaded anyway).
   */
  seam = false,
): void {
  const on = new Set<number>();
  const key = (x: number, y: number) => y * 4096 + x;
  const pts: [number, number][] = [];
  /** Pixels of the first segment (the upper arm), the part that lies against the body. */
  const root = new Set<number>();
  const widest = Math.max(1, ...parts.map(([, , w0, w1]) => Math.round(Math.max(w0, w1))));
  parts.forEach(([a, b, w0, w1], p) => {
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const w = Math.max(1, Math.round(w0 + (w1 - w0) * t));
      const X = Math.round(a.x + (b.x - a.x) * t);
      const x0 =
        outer < 0
          ? X - Math.floor(widest / 2)
          : outer > 0
            ? X - Math.floor(widest / 2) + widest - Math.min(w, widest)
            : X - Math.floor(w / 2);
      const y0 = Math.round(Math.round(a.y + (b.y - a.y) * t) - Math.floor(w / 2));
      // The limb's root (the shoulder, the hip) is rounded, not a square block: its corners are
      // left out, so where the limb leaves them bare (the top of a hanging arm) the outline turns
      // smoothly from the body's into the limb's instead of jutting out a row below it.
      const cap = p === 0 && i === 0 && w >= 4;
      for (let y = y0; y < y0 + w; y++)
        for (let x = x0; x < x0 + w; x++) {
          if (cap && (y === y0 || y === y0 + w - 1) && (x === x0 || x === x0 + w - 1)) continue;
          const k = key(x + 1024, y + 1024);
          if (on.has(k)) continue;
          on.add(k);
          if (p === 0) root.add(k);
          pts.push([x, y]);
        }
    }
  });
  const has = (x: number, y: number) => on.has(key(x + 1024, y + 1024));
  /** Open underneath — the limb's end or underside, not the lone corner a slant steps in by. */
  const under = (x: number, y: number) => has(x, y) && !has(x, y + 1);
  for (const [x, y] of pts) {
    const edge =
      !has(x - 1, y) ||
      (seam && outer < 0 && !has(x + 1, y) && has(x, y - 1) && root.has(key(x + 1024, y + 1024))) ||
      (under(x, y) && (under(x - 1, y) || under(x + 1, y)));
    g.fillStyle = edge ? shade : fill;
    g.fillRect(x, y, 1, 1);
  }
}

/** Filled ellipse from horizontal spans, crisp at the edges. */
export function blob(g: G, cx: number, cy: number, rx: number, ry: number, c: string): void {
  g.fillStyle = c;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    const dy = (y + 0.5 - cy) / ry;
    if (dy < -1 || dy > 1) continue;
    const hw = rx * Math.sqrt(1 - dy * dy);
    const x0 = Math.round(cx - hw);
    const x1 = Math.round(cx + hw);
    if (x1 > x0) g.fillRect(x0, y, x1 - x0, 1);
  }
}

/**
 * A trunk between two centre lines (`top`→`bottom`), `hw(t)` half-wide at each row (t = 0 at the
 * top, 1 at the bottom). Torsos, shirts and skirts are all trunks.
 */
export function trunk(
  g: G,
  top: Pt,
  bottom: Pt,
  hw: (t: number) => number,
  fill: string,
  edge?: { c: string; left?: number; right?: number },
): void {
  const y0 = Math.round(top.y);
  const y1 = Math.round(bottom.y);
  for (let y = y0; y <= y1; y++) {
    const t = y1 > y0 ? (y - y0) / (y1 - y0) : 0;
    const x = top.x + (bottom.x - top.x) * t;
    const w = hw(t);
    const L = Math.round(x - w);
    const R = Math.round(x + w);
    px(g, L, y, R - L + 1, 1, fill);
    if (edge?.left) px(g, L, y, edge.left, 1, edge.c);
    if (edge?.right) px(g, R - edge.right + 1, y, edge.right, 1, edge.c);
  }
}

/** `a` blended toward `b` by `k` (0–1). */
function mix(a: string, b: string, k: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(i, i + 2), 16);
  return `#${[1, 3, 5]
    .map((i) =>
      Math.round(ch(a, i) * (1 - k) + ch(b, i) * k)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** `p` moved `d` pixels toward `q`. */
export const toward = (p: Pt, q: Pt, d: number): Pt => {
  const l = Math.max(0.001, Math.hypot(q.x - p.x, q.y - p.y));
  return { x: p.x + ((q.x - p.x) / l) * d, y: p.y + ((q.y - p.y) / l) * d };
};
export const lerp = (a: Pt, b: Pt, t: number): Pt => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
export const zOf = (sk: Skeleton, j: JointName, dz = 0) => sk.joints[j].z + dz;
/** The side of the screen a limb lies on from the body's middle (`limb`'s `outer`), by its offset. */
const outerSide = (dx: number): number => (dx < -0.5 ? -1 : dx > 0.5 ? 1 : 0);

// ── Model 0: the bare body ──

export interface BodyLook {
  skin: string;
  skinS: string;
  eye: string;
  /**
   * The weapon hand holds a blade or staff, so it grips instead of making a fist — or, for a weapon
   * that is put away, only in the frames that carry it (drawing it, putting it away).
   */
  armed?: boolean | ((sk: Skeleton) => boolean);
  /** The left hand holds something too (a paired weapon, a bow). */
  armedB?: boolean;
  /** Hand shapes held whatever the pose (right `A`, left `B`); a side left out follows the pose. */
  hands?: Partial<Record<Side, HandShape>>;
  /** Plain underwear worn when nothing else covers the body (omit for the bare mannequin). */
  under?: { c: string; s: string };
}

const SKIN = { skin: '#eec7a2', skinS: skinShade('#eec7a2') };
const UNDER = tones({ inner: '#ece6da' });

export const MODEL0: BodyLook = {
  skin: SKIN.skin,
  skinS: SKIN.skinS!,
  eye: '#2a1c1a',
  under: { c: UNDER.inner, s: UNDER.innerS! },
};

/** The bare body: legs, torso, arms, hands, feet and a hairless head with the face's features. */
export function bodyLayer(b: BodyLook): Layer {
  return {
    id: 'body',
    label: 'ร่างกาย',
    pieces(sk) {
      const j = sk.joints;
      const out: Piece[] = [];
      for (const s of ['B', 'A'] as Side[]) {
        const hip = j[`hip${s}`];
        const knee = j[`knee${s}`];
        const ankle = j[`ankle${s}`];
        out.push({
          z: zOf(sk, `hip${s}`),
          draw: (g) => {
            const L = sk.shape.limb;
            limb(
              g,
              [
                [hip, knee, 5 + L, 4 + L],
                [knee, ankle, 4 + L, 3 + L],
              ],
              b.skin,
              b.skinS,
              outerSide(hip.x - j.pelvis.x),
            );
            foot(g, sk, s, b.skin, b.skinS);
          },
        });
        const sh = j[`shoulder${s}`];
        const el = j[`elbow${s}`];
        const wrist = j[`wrist${s}`];
        const shape =
          b.hands?.[s] ??
          handShape(
            sk,
            s,
            !!(s === 'A' ? (typeof b.armed === 'function' ? b.armed(sk) : b.armed) : b.armedB),
          );
        const tones = handTones(b.skin, b.skinS);
        let fineHand: HandSprite | undefined;
        const sprite = () => (fineHand ??= handSprite(sk, s, shape, tones, RIG_RES));
        out.push({
          z: zOf(sk, `shoulder${s}`),
          draw: (g) => {
            const L = sk.shape.limb;
            // The forearm stops short of the wrist so it does not fill in the hand (a grip's gap).
            limb(
              g,
              [
                [sh, el, 4 + L, 3 + L],
                [el, toward(wrist, el, 1.5), 3 + L * 0.5, 3 + L * 0.5],
              ],
              b.skin,
              b.skinS,
              outerSide(sh.x - j.chest.x),
              true,
            );
          },
        });
        // The hand is its own piece, just over whatever is worn on its arm (sleeve, cuff, guards),
        // so it comes out of the sleeve instead of being swallowed by it.
        out.push({
          z: zOf(sk, `shoulder${s}`, 0.08),
          grip: shape === 'grip',
          draw: (g) => drawHandArt(g, sprite(), RIG_RES),
          // Drawn at full resolution (hands.ts), over whatever lies behind it.
          hand: true,
          fine: (g: G, res: number) =>
            drawHand(g, res === RIG_RES ? sprite() : handSprite(sk, s, shape, tones, res)),
        });
      }
      out.push({ z: zOf(sk, 'chest'), draw: (g) => torso(g, sk, b.skin, b.skinS, 0) });
      out.push({ z: zOf(sk, 'head'), draw: (g) => head(g, sk, b) });
      return out;
    },
  };
}

/** The traced head's main skin tone (its commonest colour), which its features' skin is measured against. */
function mainTone(sp: FineSprite): string {
  const count = new Map<string, number>();
  for (const row of sp.rows) for (const ch of row) if (ch !== '.') count.set(ch, (count.get(ch) ?? 0) + 1);
  const ch = [...count].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'a';
  return sp.palette[KEYS.indexOf(ch)]!;
}

/**
 * The traced features (head2.ts, face2.ts) for the slots set to them, at full resolution, each laid
 * back where it was on the traced head: nose, mouth (unless the pose's mood has its own), brows and
 * the eyes — shut for a blink or closed eyes, their irises in the face's iris colour.
 */
function fineFeatures(g: G, sk: Skeleton, f: FaceLook, mood: Mood, skin: string, hair: string): void {
  const face = gazeFace(fineFace(sk.view, f), sk.gaze);
  const head = REF_HEAD[sk.view];
  if (!face || !head) return;
  const main = mainTone(head);
  const own = (slot: FineSlot) => isFinePart(slot, f[slot]);
  if (own('nose') && face.nose) fineSprite(g, sk, face.nose, main, skin);
  if (own('mouth') && face.mouth && (mood === 'idle' || mood === 'closed'))
    fineSprite(g, sk, face.mouth, main, skin);
  if (own('brows') && face.brows) fineSprite(g, sk, face.brows, main, skin, undefined, f.facialHair ?? hair);
  if (own('eyes')) {
    const eyes = mood === 'closed' ? face.closed : face.eyes;
    if (eyes) fineSprite(g, sk, eyes, main, skin, mood === 'closed' ? undefined : f.iris);
  }
}

/**
 * One traced sprite on the head joint at double resolution, its skin tones carried over to this
 * body's skin: each skin colour keeps its ratio to the head's main tone `main`. With `iris`, the
 * eye's iris browns (its commonest mid-dark brown and those near it) take that colour in the same
 * way. Lashes, brows and the eyes' whites keep their own colours.
 */
function fineSprite(
  g: G,
  sk: Skeleton,
  sp: FineSprite,
  main: string,
  skin: string,
  iris?: string,
  /** Hair colour for a brow: its hairs take it, each keeping how far it is from the brow's own. */
  hair?: string,
): void {
  const res = 2;
  const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const lumOf = (c: number[]) => c[0]! * 0.3 + c[1]! * 0.59 + c[2]! * 0.11;
  const scale = (c: number[], from: number[], to: number[]) =>
    `rgb(${c.map((v, i) => Math.min(255, Math.round((v * to[i]!) / Math.max(1, from[i]!)))).join(',')})`;
  const base = rgb(main);
  const want = rgb(skin);
  // The iris's own brown: the commonest colour of the eye that is neither lash-dark nor light.
  let irisFrom: number[] | undefined;
  if (iris) {
    const count = new Map<string, number>();
    for (const row of sp.rows)
      for (const ch of row) {
        if (ch === '.') continue;
        const l = lumOf(rgb(sp.palette[KEYS.indexOf(ch)]!));
        if (l >= 30 && l < 120) count.set(ch, (count.get(ch) ?? 0) + 1);
      }
    const ch = [...count].sort((a, b) => b[1] - a[1])[0]?.[0];
    if (ch) irisFrom = rgb(sp.palette[KEYS.indexOf(ch)]!);
  }
  // A brow's own hair colour: its commonest colour that is not skin.
  let hairFrom: number[] | undefined;
  if (hair) {
    const count = new Map<string, number>();
    for (const row of sp.rows)
      for (const ch of row) {
        if (ch === '.') continue;
        const c = rgb(sp.palette[KEYS.indexOf(ch)]!);
        if (!(c[0]! > c[1]! && c[1]! > c[2]! && c[0]! - c[2]! > 25 && lumOf(c) >= 95))
          count.set(ch, (count.get(ch) ?? 0) + 1);
      }
    const ch = [...count].sort((a, b) => b[1] - a[1])[0]?.[0];
    if (ch) hairFrom = rgb(sp.palette[KEYS.indexOf(ch)]!);
  }
  const colours = sp.palette.map((h) => {
    const c = rgb(h);
    const lum = lumOf(c);
    if (hairFrom && hair && !(c[0]! > c[1]! && c[1]! > c[2]! && c[0]! - c[2]! > 25 && lum >= 95)) {
      const to = rgb(hair);
      return `rgb(${c.map((v, i) => Math.max(0, Math.min(255, Math.round(to[i]! + (v - hairFrom![i]!) * 0.8)))).join(',')})`;
    }
    if (irisFrom && iris && lum >= 30 && lum < 120 && Math.abs(lum - lumOf(irisFrom)) < 45)
      return scale(c, irisFrom, rgb(iris));
    const skinLike = c[0]! > c[1]! && c[1]! > c[2]! && c[0]! - c[2]! > 25 && lum >= 95;
    return skinLike ? scale(c, base, want) : h;
  });
  const x0 = Math.round(sk.joints.head.x) * res + sp.x;
  const y0 = Math.round(sk.joints.head.y) * res + sp.y;
  sp.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const ch = row[c]!;
      if (ch === '.') continue;
      g.fillStyle = colours[KEYS.indexOf(ch)]!;
      g.fillRect(x0 + c, y0 + r, 1, 1);
    }
  });
}

/** The letters a traced sprite's rows use for its palette, in order. */
const KEYS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** Half-widths of the torso at the shoulders, waist and hips for a view. */
export function torsoWidths(sk: Skeleton): [number, number, number] {
  const { shoulder, waist, hip, belly } = sk.shape;
  if (sk.square) return [shoulder, waist, hip];
  // In profile the body's depth shows, the belly standing out; turned, a little less than head-on.
  if (sk.view === 'side') return [shoulder * 0.67, waist * 0.8 + belly, hip * 0.83];
  return [shoulder * 0.92, waist * 0.9 + belly * 0.5, hip * 0.92];
}

/** The torso from the neck to the hips, widened by `grow` pixels for clothing worn over it. */
export function torso(
  g: G,
  sk: Skeleton,
  c: string,
  s: string,
  grow: number,
  to: JointName = 'pelvis',
): void {
  const j = sk.joints;
  const [shw, whw, hhw] = torsoWidths(sk);
  // Neck: from under the chin all the way down into the shoulders, wherever they sit, with the
  // chin's shadow across its top and its shaded side.
  if (!grow) {
    const top = Math.round(j.neck.y) - 2;
    const h = Math.max(1, Math.round(j.chest.y) - top);
    // Broad shoulders carry a neck four pixels wide, as wide as the chin above it.
    const w = shw >= 7 ? 4 : 3;
    const x = j.neck.x - (w === 4 ? 2 : 1);
    px(g, x, top, w, h, c);
    px(g, x, top + 2, w, 1, s);
    px(g, x, top, 1, h, s);
  }
  const rows = Math.max(1, j.waist.y - j.chest.y);
  // The shoulders slope down from the base of the neck: one row narrower, then their full width.
  const chestToWaist = (t: number) => Math.min(shw + (whw - shw) * t, shw - 1 + t * rows) + grow;
  // Bare skin keeps a single line of shade down its far side; cloth over it a broader fold.
  const edge = { c: s, left: grow ? 2 : 1 };
  trunk(g, j.chest, j.waist, chestToWaist, c, edge);
  if (to === 'pelvis') {
    const waistToHip = (t: number) => whw + (hhw - whw) * t + grow;
    trunk(g, j.waist, j.pelvis, waistToHip, c, edge);
  }
  // The base of the neck, where the slope of the shoulders begins.
  px(g, j.chest.x - 2 - Math.round(grow), j.chest.y - 1, 5 + Math.round(grow) * 2, 1, c);
  paintCurves(g, sk, grow, 'bust', c, s);
  if (to === 'pelvis') paintCurves(g, sk, grow, 'seat', c, s, Math.round(j.pelvis.y));
}

/** A run of pixels on one row; `shade` runs are the shadow that shapes a curve seen head-on. */
export interface Span {
  y: number;
  x0: number;
  x1: number;
  shade?: boolean;
}

/** The torso's left and right columns on row `y`, as `torso()` draws them with `grow`. */
export function torsoEdges(sk: Skeleton, grow: number, y: number): [number, number] {
  const j = sk.joints;
  const [shw, whw, hhw] = torsoWidths(sk);
  const rows = Math.max(1, j.waist.y - j.chest.y);
  let x: number;
  let w: number;
  if (y <= j.waist.y) {
    const t = Math.max(0, Math.min(1, (y - j.chest.y) / Math.max(1, j.waist.y - j.chest.y)));
    x = j.chest.x + (j.waist.x - j.chest.x) * t;
    w = Math.min(shw + (whw - shw) * t, shw - 1 + t * rows) + grow;
  } else {
    const t = Math.max(0, Math.min(1, (y - j.waist.y) / Math.max(1, j.pelvis.y - j.waist.y)));
    x = j.waist.x + (j.pelvis.x - j.waist.x) * t;
    w = whw + (hhw - whw) * Math.min(1, t) + grow;
  }
  return [Math.round(x - w), Math.round(x + w)];
}

/**
 * The bust or the seat as runs of pixels beyond (or shading on) the torso. In profile and turned
 * three-quarter the chest stands out in front (+x, the way the figure faces) and the seat behind;
 * head-on the bust shows as shadow under each side of the chest, and from behind the seat as the
 * shadow between and under its two halves. `grow` matches the garment being drawn, so clothes
 * follow the same curves.
 */
export function curveSpans(sk: Skeleton, grow: number, part: 'bust' | 'seat'): Span[] {
  const n = (part === 'bust' ? sk.shape.bust : sk.shape.seat) ?? 0;
  if (n <= 0) return [];
  const j = sk.joints;
  const [shw, , hhw] = torsoWidths(sk);
  const out: Span[] = [];
  const h = part === 'bust' ? 3 + Math.round(n) : 4 + Math.round(n);
  const top = Math.round(part === 'bust' ? j.chest.y + 2 : j.pelvis.y - 2);
  const turned = !sk.square;
  const reach = (part === 'bust' ? 0.75 : 0.6) * (sk.view === 'side' ? 1 : 0.6);
  for (let i = 0; i < h; i++) {
    const y = top + i;
    const prof = Math.sin((Math.PI * (i + 0.5)) / h);
    const [L, R] = torsoEdges(sk, grow, Math.min(y, Math.round(j.pelvis.y)));
    if (turned) {
      const p = Math.round(prof * n * reach + (part === 'seat' && y > j.pelvis.y ? 0.4 : 0));
      if (p <= 0) continue;
      if (part === 'bust' && !sk.back) out.push({ y, x0: R + 1, x1: R + p });
      if (part === 'seat') out.push({ y, x0: L - p, x1: L - 1 });
      continue;
    }
    // Head-on: a fuller figure is a touch wider at the fullest rows.
    if (n >= 3 && prof > 0.8 && (part === 'bust' ? !sk.back : true)) {
      out.push({ y, x0: L - 1, x1: L - 1 });
      out.push({ y, x0: R + 1, x1: R + 1 });
    }
  }
  if (!turned) {
    const cx = Math.round(part === 'bust' ? j.chest.x : j.pelvis.x);
    const half = Math.max(2, Math.round((part === 'bust' ? shw : hhw) * 0.55));
    const under = top + h - 1;
    if (part === 'bust' && !sk.back) {
      out.push({ y: under, x0: cx - half, x1: cx - 1, shade: true });
      out.push({ y: under, x0: cx + 1, x1: cx + half, shade: true });
      if (n >= 2) out.push({ y: under - 1, x0: cx, x1: cx, shade: true });
    }
    if (part === 'seat' && sk.back) {
      for (let y = Math.round(j.pelvis.y) - 1; y <= under; y++) out.push({ y, x0: cx, x1: cx, shade: true });
      out.push({ y: under, x0: cx - half, x1: cx - 2, shade: true });
      out.push({ y: under, x0: cx + 2, x1: cx + half, shade: true });
    }
  }
  return out;
}

/** Paints the bust or seat of a body or garment in its colour `c` (shading in `s`). */
export function paintCurves(
  g: G,
  sk: Skeleton,
  grow: number,
  part: 'bust' | 'seat',
  c: string,
  s: string,
  /** The garment's last row: curves stop at its hem. */
  maxY = Infinity,
): void {
  for (const sp of curveSpans(sk, grow, part))
    if (sp.y <= maxY) px(g, sp.x0, sp.y, sp.x1 - sp.x0 + 1, 1, sp.shade ? s : c);
}

/**
 * The foot as three rows, heel to sole. Flat on the ground the heel sits under the ankle and the
 * toes point ahead (toward the camera head-on); lifted off it in a stride, the heel rises and the
 * toes hang down and forward, as a foot pushing off does. Socks and shoes are drawn on these rows.
 */
export function footRows(sk: Skeleton, side: Side): { x: number; y: number; w: number }[] {
  const an = sk.joints[`ankle${side}`];
  const lifted = GROUND - 3 - an.y >= 1.5;
  if (sk.square)
    return lifted
      ? [
          { x: an.x - 2, y: an.y, w: 4 },
          { x: an.x - 2, y: an.y + 1, w: 4 },
          { x: an.x - 1.5, y: an.y + 2, w: 3 },
        ]
      : [0, 1, 2].map((r) => ({ x: an.x - 2, y: an.y + r, w: 4 }));
  return lifted
    ? [
        { x: an.x - 1, y: an.y, w: 3 },
        { x: an.x, y: an.y + 1, w: 4 },
        { x: an.x + 2, y: an.y + 2, w: 3 },
      ]
    : // The instep narrow at the ankle, the toes reaching forward along the ground.
      [4, 5, 6].map((w, r) => ({ x: an.x - 1, y: an.y + r, w }));
}

function foot(g: G, sk: Skeleton, side: Side, c: string, s: string): void {
  footRows(sk, side).forEach((r, i) => px(g, r.x, r.y, r.w, 1, i === 2 ? s : c));
}

/** The head: its grid (heads.ts) for the view stamped on the head joint (features are the face layer's). */
function head(g: G, sk: Skeleton, b: BodyLook): void {
  const h = sk.joints.head;
  const t = HEAD;
  const x = Math.round(h.x);
  const y = Math.round(h.y);
  // Skin, ear shade, cheekbone light and the soft hollow under it.
  const colours: Record<string, string> = {
    s: b.skin,
    e: b.skinS,
    c: shade(b.skin, 0.05),
    S: mix(b.skin, b.skinS, 0.3),
  };
  t.rows[sk.view].forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const col = colours[row[c]!];
      if (col) px(g, x - t.ox + c, y - t.oy + r, 1, 1, col);
    }
  });
}

/** How far a row of a hanging grid is moved, in pixels. */
type Offset = { dx: number; dy: number };

/**
 * Stamps a pixel grid with its origin cell on (`x`, `y`), one colour per grid letter. `bend` moves
 * each row; when consecutive rows are pushed more than a pixel apart sideways, the row is smeared
 * across the gap so a swung strand stays one piece.
 */
function stamp(
  g: G,
  grid: Grid,
  x: number,
  y: number,
  colours: Readonly<Record<string, string>>,
  bend?: (row: number) => Offset,
): void {
  let prev = 0;
  grid.rows.forEach((row, r) => {
    const { dx, dy } = bend ? bend(r) : { dx: 0, dy: 0 };
    const step = Math.sign(dx - prev) || 1;
    for (let sx = r && dx !== prev ? prev + step : dx; step > 0 ? sx <= dx : sx >= dx; sx += step)
      for (let c = 0; c < row.length; c++) {
        const col = colours[row[c]!];
        if (col) px(g, x - grid.ox + c + sx, y - grid.oy + r + dy, 1, 1, col);
      }
    prev = dx;
  });
}

/**
 * Where loose hair or cloth hangs at `d` rows below its root, `k` of the way to the tip. The pose's
 * fabric sway (walking, running, leaping) bends it back behind the figure and, past a brisk walk,
 * swings the whole strand up about its root toward the horizontal; a wave runs down it through the
 * pose's cycle — a slow breeze at rest, a quicker ripple in a stride. Head-on there is no behind,
 * so it only swings from side to side. `gain` softens it for short, stiff strands.
 */
export function flutter(sk: Skeleton, k: number, d: number, gain = 1): Offset {
  const stride = sk.pose === 'walk' || sk.pose === 'sprint';
  const cycle = stride ? WALK_FRAMES : sk.pose === 'idle' || sk.pose === 'ready' ? FRAMES : 0;
  const a = cycle ? (sk.phase / cycle) * Math.PI * 2 : 0;
  const wave = Math.sin(a - k * 3) * k * (stride ? 1.6 : 0.9) * gain;
  if (sk.square) return { dx: Math.round(wave), dy: 0 };
  const sway = sk.rig.sway;
  // Lift: the strand's angle back from hanging straight down, up to about 50°.
  const lift = Math.min(0.9, Math.max(0, (sway - 1) * 0.35)) * gain;
  const trail = -k * k * sway * 2.5 * gain * (1 - lift);
  return {
    dx: Math.round(-Math.sin(lift) * d + trail + wave),
    dy: Math.round((Math.cos(lift) - 1) * d),
  };
}

/** A bend for a grid hanging from row `root`: rows below it follow `flutter`, rows above stay put. */
function hanging(sk: Skeleton, grid: Grid, root: number, gain = 1): (row: number) => Offset {
  const len = Math.max(1, grid.rows.length - 1 - root);
  return (r) => (r <= root ? { dx: 0, dy: 0 } : flutter(sk, (r - root) / len, r - root, gain));
}

/** A hairstyle's lacquer-red ribbon (`r`) and its fold (`R`). */
const RIBBON = { r: '#b8323c', R: '#7c1e2a' } as const;

/**
 * A hairstyle: the cap over the skull just above the head, the locks by the face over it, the bun
 * on the crown, and the tail behind the body when the face is toward the camera or over the back
 * when it is turned away. Locks and tail sway with the figure's movement.
 */
export function hairLayer(styleId: string | HairStyle, hair: string): Layer {
  // A style no longer drawn (an old save, a stale id) falls back to the default.
  const style = typeof styleId === 'string' ? (HAIR_BY_ID[styleId] ?? HAIR_BY_ID[DEFAULT_HAIR]!) : styleId;
  // Base, sheen and strand line; styles that draw their own light (`HairStyle.lit`) add a deep
  // shadow and a lacquer-red ribbon with its fold.
  const colours = {
    h: hair,
    H: shade(hair, 0.16),
    d: shade(hair, -0.05),
    D: shade(hair, -0.14),
    ...RIBBON,
  };
  return {
    id: 'hair',
    label: 'ผม',
    hair: { style: style.id, colour: hair },
    anchors: (view) => style.anchors[view],
    pieces(sk, fit) {
      const h = sk.joints.head;
      const x = Math.round(h.x);
      const y = Math.round(h.y);
      // A one-sided style on a left-facing view draws its mirror image's parts (rig/leftArt.ts).
      const cap = gridFor(style.cap[sk.view], sk, style.left?.cap);
      const out: Piece[] = cap ? [{ z: h.z + 0.02, draw: (g) => stamp(g, cap, x, y, colours) }] : [];
      const locks = gridFor(style.locks?.[sk.view], sk, style.left?.locks);
      if (locks) {
        const bend = hanging(sk, locks, 0, 0.5);
        out.push({
          z: h.z + 0.022,
          draw: (g) => stamp(g, locks, x, y, colours, bend),
        });
      }
      const bun = gridFor(style.bun?.[sk.view], sk, style.left?.bun);
      if (bun && !fit.hidden.has('bun'))
        out.push({ z: h.z + 0.021, draw: (g) => stamp(g, bun, x, y, colours) });
      const tail = gridFor(style.tail?.[sk.view], sk, style.left?.tail);
      if (tail && !fit.hidden.has('tail')) {
        const bend = hanging(sk, tail, 0);
        out.push({
          z: sk.back ? h.z + 0.03 : -0.95,
          draw: (g) => stamp(g, tail, x, y, colours, bend),
        });
      }
      return out;
    },
  };
}

/**
 * A piece of headwear on its anchor: the hairstyle's crown, each of its buns, or the head itself.
 * A negative depth puts it behind the body (ribbons trailing down the back).
 */
export function headwearLayer(id: string, colours?: Readonly<Record<string, string>>): Layer {
  const item = HEADWEAR_BY_ID[id];
  return {
    id: `headwear:${id}`,
    label: item?.label ?? id,
    hides: item?.hides,
    pieces(sk, fit) {
      if (!item) return [];
      const h = sk.joints.head;
      // Worn on one side: on a left-facing view, as the mirror image wears it (rig/leftArt.ts).
      const grid = gridFor(item.grids[sk.view], sk, item.left);
      if (!grid) return [];
      const dz = sk.back ? item.z.back : item.z.front;
      const z = dz < 0 ? dz : h.z + dz;
      const points =
        item.anchor === 'head'
          ? [[0, 0] as const]
          : item.anchor === 'buns' && fit.anchors.buns.length
            ? fit.anchors.buns
            : [fit.anchors.crown];
      return points.map(([ax, ay]) => ({
        z,
        draw: (g: G) =>
          stamp(
            g,
            grid,
            Math.round(h.x + ax),
            Math.round(h.y + ay),
            colours ?? item.colours,
            item.flutters ? hanging(sk, grid, grid.oy) : undefined,
          ),
      }));
    },
  };
}

/**
 * The face: eyes, brows, nose, mouth, ears and marks on the skin just over the head, and facial
 * hair and earrings over the hair (so a beard is not hidden by long locks). The pose's mood picks
 * the expression; a closed-eyes pose (meditation) shuts them.
 */
export function faceLayer(f: FaceLook, skin: { skin: string; skinS: string }, hair: string): Layer {
  // The hair colour, for the brows (the name `hair` is taken below by the facial hair's stamps).
  const hairTone = hair;
  const fh = f.facialHair ?? hair;
  const colours: Record<string, string> = {
    k: '#1a1010',
    w: f.sclera,
    i: f.iris,
    P: shade(f.iris, -0.18),
    I: '#ffffff',
    b: shade(fh, -0.04),
    n: skin.skinS,
    e: skin.skinS,
    m: '#7a3a32',
    L: '#c8404e',
    M: '#4a1418',
    t: '#f6f2ec',
    h: fh,
    f: '#a8755a',
    r: '#d0605a',
    B: '#eaa098',
    o: '#3a2420',
    x: '#c98676',
    p: '#d0303c',
    g: '#e8c27e',
    G: '#c9ced8',
    j: '#5fbf9a',
    W: '#ece6da',
    s: skin.skin,
    H: shade(fh, -0.1),
    u: '#2f4a7a',
    c: '#7a5a8a',
    y: '#6a5a4a',
  };
  return {
    id: 'face',
    label: 'ใบหน้า',
    pieces(sk) {
      const h = sk.joints.head;
      const x = Math.round(h.x);
      const y = Math.round(h.y);
      const mood: Mood = sk.rig.closed ? 'closed' : sk.rig.face;
      const { inner, hair } = faceStamps(f, sk.view, mood);
      const draw = (list: typeof inner) => (g: G) => {
        for (const st of list) stamp(g, { ox: -st.x, oy: -st.y, rows: st.rows }, x, y, colours);
      };
      const refs = (['eyes', 'brows', 'nose', 'mouth'] as const).some((slot) => isFinePart(slot, f[slot]));
      return [
        {
          z: h.z + 0.01,
          draw: draw(inner),
          // The traced features, for the slots set to them, at full resolution.
          ...(refs && {
            fine: (g: G, res: number) => res === 2 && fineFeatures(g, sk, f, mood, skin.skin, hairTone),
          }),
        },
        { z: h.z + 0.025, draw: draw(hair) },
      ];
    },
  };
}

/** Plain underwear: a sleeveless vest and knee-length trousers, the default when a slot is empty. */
export function underLayer(u: { c: string; s: string }): Layer {
  return {
    id: 'under',
    label: 'ชุดชั้นใน',
    pieces(sk) {
      const j = sk.joints;
      const out: Piece[] = [
        { z: zOf(sk, 'chest', 0.01), draw: (g) => torso(g, sk, u.c, u.s, 0.5, 'pelvis') },
      ];
      for (const s of ['B', 'A'] as Side[]) {
        const hip = j[`hip${s}`];
        const knee = j[`knee${s}`];
        out.push({
          z: zOf(sk, `hip${s}`, 0.01),
          draw: (g) => segment(g, hip, lerp(hip, knee, 0.85), 6 + sk.shape.limb, 5 + sk.shape.limb, u.c, u.s),
        });
      }
      return out;
    },
  };
}

// ── composing ──

/** Canvas size and feet anchor shared with the classic figures, so the two can be laid over each other. */
export const FRAME = {
  w: ART_CX * 2 + 8,
  h: 72 + PAD_TOP + BODY_EXTRA,
  ax: ART_CX,
  ay: GROUND + 1 + PAD_TOP,
};

/** Rig figures are drawn at double resolution: two canvas pixels per art pixel (see `artW`). */
export const RIG_RES = 2;

export interface ComposeOptions {
  /** Canvas pixels per art pixel: 2 draws the figure at double resolution (rig/realism.ts). */
  res?: 1 | 2;
}

/**
 * One frame of a figure in the game's flat finish (rig/realism.ts): the pieces as drawn at `res`,
 * with no added light or outline.
 */
export function compose(layers: readonly Layer[], sk: Skeleton, opt: ComposeOptions = {}): HTMLCanvasElement {
  return hasNative(layers) ? composeNative(layers, sk, opt.res) : composeReal(layers, sk, opt.res);
}

/** Model 0 for one frame: the bare body, in underwear unless `bare`. */
export function model0(view: View, pose: Pose, ph = 0, opt: ComposeOptions & { bare?: boolean } = {}) {
  const layers = [bodyLayer(MODEL0)];
  if (!opt.bare && MODEL0.under) layers.push(underLayer(MODEL0.under));
  return compose(layers, skeleton(view, pose, ph), opt);
}

/** Lines and dots for every bone and joint, drawn over a figure at `zoom` with feet at (x, y). */
export function drawSkeleton(g: G, sk: Skeleton, x: number, y: number, zoom: number, flip = false): void {
  const P = (j: Joint) => ({
    X: x + (flip ? -1 : 1) * (j.x + 0.5 - FRAME.ax) * zoom,
    Y: y + (j.y + 0.5 + PAD_TOP - FRAME.ay) * zoom,
  });
  g.save();
  g.lineWidth = Math.max(1, zoom * 0.4);
  for (const [a, b] of BONES) {
    const A = P(sk.joints[a]);
    const B = P(sk.joints[b]);
    // Near bones gold, far bones blue, the spine white.
    const z = sk.joints[b].z;
    g.strokeStyle =
      z >= 0.5 ? 'rgb(255 214 90 / 0.95)' : z < 0 ? 'rgb(120 190 255 / 0.9)' : 'rgb(255 255 255 / 0.9)';
    g.beginPath();
    g.moveTo(A.X, A.Y);
    g.lineTo(B.X, B.Y);
    g.stroke();
  }
  for (const j of Object.values(sk.joints)) {
    const { X, Y } = P(j);
    g.fillStyle = 'rgb(255 70 70 / 0.95)';
    g.fillRect(X - zoom * 0.6, Y - zoom * 0.6, zoom * 1.2, zoom * 1.2);
  }
  g.restore();
}
